/**
 * Modal de copie d'un créneau EDT vers d'autres classes.
 * Toutes les classes cochées sont ajoutées au tronc commun (créé si absent).
 */
import { useState } from 'react';
import { X, Copy, Check, Loader2 } from 'lucide-react';
import { useCopierEdtMutation } from '../../services/vieScolaireApi';

export default function ModalCopier({
  source, classesDisponibles, onClose, onCopied,
}: {
  source: any;
  classesDisponibles: string[];
  onClose: () => void;
  onCopied: () => void;
}) {
  const [choix, setChoix] = useState<string[]>([]);
  const [copier, { isLoading }] = useCopierEdtMutation();

  const toggle = (c: string) =>
    setChoix(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);

  const handleSubmit = async () => {
    if (choix.length === 0) return;
    try {
      const res = await copier({ edtId: source.id, classes: choix }).unwrap();
      const nbCrees = res.crees?.length || 0;
      const nbIgnorees = res.classes_ignorees?.length || 0;
      alert(`Copie effectuée : ${nbCrees} nouvelles classes ajoutées${nbIgnorees ? ` (${nbIgnorees} déjà présentes)` : ''}.`);
      onCopied();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white dark:bg-gray-900 px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
          <div>
            <div className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Copy size={16} /> Copier vers d'autres classes
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {source.libelle_matiere} • {source.classe} • {source.jour} {source.heure_debut}–{source.heure_fin}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X size={18} />
          </button>
        </header>

        <div className="p-5">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Cochez les classes qui doivent suivre exactement le même cours (mêmes horaires, formateur, salle). Elles seront regroupées en <strong>tronc commun</strong>.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {classesDisponibles.map(c => {
              const actif = choix.includes(c);
              return (
                <button key={c} onClick={() => toggle(c)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition inline-flex items-center justify-center gap-1 ${
                    actif
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-blue-400'
                  }`}>
                  {actif && <Check size={11} />} {c}
                </button>
              );
            })}
            {classesDisponibles.length === 0 && (
              <span className="col-span-full text-[11px] text-gray-500">
                Aucune autre classe disponible pour ce niveau.
              </span>
            )}
          </div>
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg">
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={isLoading || choix.length === 0}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg inline-flex items-center gap-2">
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
            Copier ({choix.length})
          </button>
        </footer>
      </div>
    </div>
  );
}
